import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { SERVICIO_OPTIONS } from "../../data/site";
import { EASE } from "../../lib/motion";
import emailjs from "@emailjs/browser";
const schema = z.object({
  nombre: z.string().min(2, "Ingresa tu nombre"),
  empresa: z.string().min(2, "Ingresa tu empresa"),
  email: z.string().email("Correo electrónico inválido"),
  telefono: z.string().optional(),
  servicio: z.string().min(1, "Selecciona un servicio"),
  mensaje: z.string().min(10, "Cuéntanos un poco más (mínimo 10 caracteres)"),
});

const inputCls =
  "w-full rounded-xl border border-input bg-card px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/60 transition-colors duration-200 focus:border-electric focus:outline-none";
const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground";

export default function ContactForm() {
  const [sent, setSent] = useState(null);
  const [serverError, setServerError] = useState("");
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema), defaultValues: { servicio: "" } });


  const onSubmit = async (values) => {
    setServerError("");

    console.log(values)
    try {
      const result = await emailjs.send(
        'service_ipcq81c',
        'template_yd5uc9b',
        {
          from_name: values.nombre,
          from_email: values.email,
          from_phone: values.telefono || "",
          from_company: values.empresa || "",
          to_name: values.servicio || "",
          from_message: values.mensaje,
        },
        '6Gi27dCPf7x1T35No'
      );

      console.log("EmailJS:", result);

      setSent(values);
      reset();
    } catch (e) {
      console.error("EmailJS error:", e);

      setServerError(
        e?.text ||
        e?.message ||
        "No se pudo enviar la solicitud. Inténtalo de nuevo."
      );
    }
  };


  if (sent) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: EASE }}
        className="flex h-full flex-col items-center justify-center rounded-3xl border border-emerald-500/30 bg-card p-10 text-center"
        data-testid="contact-success-panel"
      >
        <CheckCircle2 className="h-14 w-14 text-emerald-500" strokeWidth={1.5} />
        <h3 className="mt-6 font-display text-2xl font-bold">¡Solicitud recibida!</h3>
        <p className="mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground">
          Gracias {sent.nombre.split(" ")[0]}. Un especialista de DASOFT te contactará muy pronto
          sobre <strong className="text-foreground">{sent.servicio}</strong>.
        </p>
        <button
          onClick={() => setSent(null)}
          data-testid="contact-reset-button"
          className="mt-8 rounded-full border border-border px-6 py-2.5 text-sm font-semibold transition-colors hover:border-electric/50 hover:text-electric"
        >
          Enviar otra solicitud
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="rounded-3xl border border-border bg-card p-6 shadow-card sm:p-8" data-testid="contact-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="cf-nombre" className={labelCls}>Nombre *</label>
          <input id="cf-nombre" {...register("nombre")} className={inputCls} placeholder="Tu nombre" data-testid="contact-input-nombre" aria-invalid={!!errors.nombre} />
          {errors.nombre && <p className="mt-1.5 text-xs text-destructive" data-testid="contact-error-nombre">{errors.nombre.message}</p>}
        </div>
        <div>
          <label htmlFor="cf-empresa" className={labelCls}>Empresa *</label>
          <input id="cf-empresa" {...register("empresa")} className={inputCls} placeholder="Nombre de tu empresa" data-testid="contact-input-empresa" aria-invalid={!!errors.empresa} />
          {errors.empresa && <p className="mt-1.5 text-xs text-destructive">{errors.empresa.message}</p>}
        </div>
        <div>
          <label htmlFor="cf-email" className={labelCls}>Email *</label>
          <input id="cf-email" type="email" {...register("email")} className={inputCls} placeholder="nombre@empresa.com" data-testid="contact-input-email" aria-invalid={!!errors.email} />
          {errors.email && <p className="mt-1.5 text-xs text-destructive">{errors.email.message}</p>}
        </div>
        <div>
          <label htmlFor="cf-telefono" className={labelCls}>Teléfono</label>
          <input id="cf-telefono" type="tel" {...register("telefono")} className={inputCls} placeholder="Opcional" data-testid="contact-input-telefono" />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="cf-servicio" className={labelCls}>Servicio de interés *</label>
          <select id="cf-servicio" {...register("servicio")} className={inputCls} data-testid="contact-select-servicio" aria-invalid={!!errors.servicio}>
            <option value="" disabled>Selecciona una opción</option>
            {SERVICIO_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
          {errors.servicio && <p className="mt-1.5 text-xs text-destructive">{errors.servicio.message}</p>}
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="cf-mensaje" className={labelCls}>Mensaje *</label>
          <textarea id="cf-mensaje" rows={4} {...register("mensaje")} className={`${inputCls} resize-none`} placeholder="Cuéntanos sobre tu proyecto o necesidad" data-testid="contact-input-mensaje" aria-invalid={!!errors.mensaje} />
          {errors.mensaje && <p className="mt-1.5 text-xs text-destructive">{errors.mensaje.message}</p>}
        </div>
      </div>

      {serverError && (
        <p className="mt-4 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive" data-testid="contact-error-server">
          {serverError}
        </p>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        data-testid="contact-submit-button"
        className="group mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-brand-foreground shadow-glow-sm transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-strong disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        {isSubmitting ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> Enviando…</>
        ) : (
          <>Enviar solicitud <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" /></>
        )}
      </button>
    </form>
  );
}
